// Shared surface PRIMITIVES — the small building blocks every surface reuses, each mapped 1:1 onto the
// handoff's own class vocabulary (brand/ritona-app-handoff app.css: .stat-strip/.stat, .group-head/
// .h-section, .pill + .dot, .panel, .btn). The classes live in the ported handoff stylesheet and reference
// governed tokens only (G-181). Kept here so each surface component stays small (G-085).

import type { CSSProperties, ReactNode } from 'react';

import { Icon } from '../app/Icon.tsx';

/** The LEGACY screen header (eyebrow + voice title + sub) — the pre-toolbar chrome the Work-section dev
 *  harness still composes its previews with. Live dashboard surfaces use the toolbar chrome instead
 *  (app/screen-chrome.tsx). */
export function ScreenHeader({
  title,
  sub,
  right,
}: {
  readonly title: string;
  readonly sub?: string;
  readonly right?: ReactNode;
}): React.JSX.Element {
  return (
    <div className="mb-6 flex items-end justify-between gap-4">
      <div>
        <div className="ritona-numeric text-xs uppercase tracking-eyebrow text-forest">Ritona</div>
        <h1 className="ritona-voice mt-2 text-2xl font-medium tracking-snug text-text">{title}</h1>
        {sub !== undefined ? <p className="mt-1 text-sm text-text-muted">{sub}</p> : null}
      </div>
      {right !== undefined ? <div className="flex items-center gap-3">{right}</div> : null}
    </div>
  );
}

/** A section heading row (prototype .group-head): the sans .h-section title + an optional right side. */
export function SectionHead({
  label,
  right,
}: {
  readonly label: string;
  readonly right?: ReactNode;
}): React.JSX.Element {
  return (
    <div className="group-head">
      <h2 className="h-section">{label}</h2>
      {right !== undefined ? right : null}
    </div>
  );
}

/** The tone of a status dot/pill — go (Forest), attention (Bracken pending), blocked (Clay), neutral. */
export type Tone = 'go' | 'attention' | 'blocked' | 'neutral';

/** The governed colour token each tone's dot carries (matches the prototype's pill dot colours). */
const DOT_VAR: Readonly<Record<Tone, string>> = {
  go: 'var(--forest)',
  attention: 'var(--bracken)',
  blocked: 'var(--clay)',
  neutral: 'var(--mist)',
};

/** A small status dot in a governed tone (radius-full is allowed for status dots — design brief §3). */
export function Dot({ tone }: { readonly tone: Tone }): React.JSX.Element {
  return <span aria-hidden="true" className="dot-mark" style={{ background: DOT_VAR[tone] }} />;
}

/** The prototype pill class for each tone (.pill go / pending / blocked / neutral). */
const PILL_CLASS: Readonly<Record<Tone, string>> = {
  go: 'pill go',
  attention: 'pill pending',
  blocked: 'pill blocked',
  neutral: 'pill neutral',
};

/** A quiet status pill (prototype .pill — radius-sm control shape, never a rounded-full pill). */
export function StatusPill({
  tone,
  children,
}: {
  readonly tone: Tone;
  readonly children: ReactNode;
}): React.JSX.Element {
  return (
    <span className={PILL_CLASS[tone]}>
      <span className="dot" aria-hidden="true" />
      {children}
    </span>
  );
}

/** The voice-face style the prototype applies inline to a NON-numeric stat figure (screens.js statStrip). */
const STAT_VOICE: CSSProperties = {
  fontFamily: 'var(--font-voice)',
  fontSize: '1.35rem',
  letterSpacing: '-0.01em',
};

/** One stat in the strip (prototype .stat): mono label above, mono figure, muted note below. */
export function StatCard({
  label,
  value,
  note,
  numeric = true,
  dotTone,
}: {
  readonly label: string;
  readonly value: ReactNode;
  readonly note?: string;
  /** False renders the figure in the voice face (the prototype's non-numeric stats, e.g. "All clear"). */
  readonly numeric?: boolean;
  /** An optional status dot beside the figure (the prototype's tone dot on a stat). */
  readonly dotTone?: Tone | undefined;
}): React.JSX.Element {
  return (
    <div className="stat">
      <div className="mono-label">{label}</div>
      <div className="num" style={numeric ? undefined : STAT_VOICE}>
        {value}
        {dotTone !== undefined ? (
          <span className="dot" style={{ background: DOT_VAR[dotTone] }} aria-hidden="true" />
        ) : null}
      </div>
      {note !== undefined ? <div className="note">{note}</div> : null}
    </div>
  );
}

/** The flat stat strip (prototype .stat-strip): hairline dividers between stats, space-8 below. */
export function StatStrip({ children }: { readonly children: ReactNode }): React.JSX.Element {
  return (
    <div className="stat-strip" style={{ marginBottom: 'var(--space-8)' }}>
      {children}
    </div>
  );
}

/** A flat bordered working panel (prototype .panel — data lives in a surface, not on the page). An optional
 *  `data-*` marker (name → value) lets a surface put its behavioural test signal on the panel itself. */
export function Panel({
  children,
  className,
  dataMarker,
}: {
  readonly children: ReactNode;
  readonly className?: string;
  /** A single `data-<name>=<value>` attribute to place on the panel (a behavioural signal, never copy). */
  readonly dataMarker?: { readonly name: string; readonly value: string };
}): React.JSX.Element {
  const marker = dataMarker !== undefined ? { [`data-${dataMarker.name}`]: dataMarker.value } : {};
  return (
    <div className={className !== undefined ? `panel ${className}` : 'panel'} {...marker}>
      {children}
    </div>
  );
}

/** An honest empty-state line (P-SELF — the surface says what is absent, never fabricates a row). */
export function EmptyState({ children }: { readonly children: ReactNode }): React.JSX.Element {
  return <div className="empty-line">{children}</div>;
}

/** A prototype button (.btn / .btn.ghost / .btn.quiet, size .sm). Forest is the ONLY action colour and a
 *  view carries at most one primary — everything else renders ghost/quiet (design brief §3). */
export function Btn({
  kind = 'primary',
  sm = false,
  type = 'button',
  disabled,
  onClick,
  ariaLabel,
  ariaExpanded,
  children,
}: {
  readonly kind?: 'primary' | 'ghost' | 'quiet';
  readonly sm?: boolean;
  readonly type?: 'button' | 'submit';
  readonly disabled?: boolean;
  readonly onClick?: () => void;
  readonly ariaLabel?: string;
  readonly ariaExpanded?: boolean;
  readonly children: ReactNode;
}): React.JSX.Element {
  const cls = `btn${kind === 'primary' ? '' : ` ${kind}`}${sm ? ' sm' : ''}`;
  return (
    <button
      type={type}
      className={cls}
      disabled={disabled}
      onClick={onClick}
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
    >
      {children}
    </button>
  );
}

/** A directional trend arrow (up / down / flat) rendered as a governed-colour INLINE STROKE SVG — direction
 *  only (G-106). No unicode geometric glyph as an icon (G-181, design brief §3): it draws the shell's
 *  `trend-up`/`trend-down`/`trend-flat` stroke glyph in the direction's governed tone. */
export function TrendArrow({
  direction,
}: {
  readonly direction: 'up' | 'down' | 'flat';
}): React.JSX.Element {
  const iconName =
    direction === 'up' ? 'trend-up' : direction === 'down' ? 'trend-down' : 'trend-flat';
  const tone =
    direction === 'up' ? 'text-forest' : direction === 'down' ? 'text-bracken' : 'text-slate';
  const label =
    direction === 'up' ? 'trending up' : direction === 'down' ? 'trending down' : 'flat';
  return (
    <span
      className={`inline-flex items-center ${tone}`}
      role="img"
      aria-label={label}
      data-trend={direction}
    >
      <Icon name={iconName} size={16} />
    </span>
  );
}
